import {
  mapiSenderAddressFieldDisplayOrder,
  quoteFeeRequestDefaults,
} from "../../../../constants/send.constant";
import { normalizeUsCountrySubdivisionCode } from "../../../../helper/mapiSubdivisionCode";
import type { JourneyData } from "../models/journeyData";
import type { UpdateTransactionRequest } from "../models/updateTransactionRequest";

function dotKeysToNested(flatObj: Record<string, string>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [dotKey, value] of Object.entries(flatObj)) {
    const parts = dotKey.split(".");
    let current: any = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
  }
  return result;
}

function toIsoDateOfBirth(dob: string): string {
  const parts = dob.split("/");
  if (parts.length !== 3) return dob;
  return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}T00:00:00.000Z`;
}

export function buildUpdateTransactionRequest(
  journey: JourneyData,
  agentPartnerId: string
): UpdateTransactionRequest | null {
  const transactionId = journey.selectedServiceOption?.transactionId;
  if (!transactionId || !journey.destinationCountry?.iso3Code) {
    return null;
  }

  const option = journey.selectedServiceOption as any;
  const flat: Record<string, string> = {};
  const {
    senderInfo,
    receiverInfo,
    senderAddress,
    senderPersonalDetails,
    senderPrimaryIdentification,
    senderSecondaryIdentification,
    phoneNumber,
    additionalInfo,
    additionalFields,
  } = journey;

  if (senderInfo.firstName) flat["sender.name.firstName"] = senderInfo.firstName;
  if (senderInfo.middleName) flat["sender.name.middleName"] = senderInfo.middleName;
  if (senderInfo.lastName) flat["sender.name.lastName"] = senderInfo.lastName;
  if (senderInfo.secondLastName) flat["sender.name.secondLastName"] = senderInfo.secondLastName;

  if (receiverInfo.firstName) flat["receiver.name.firstName"] = receiverInfo.firstName;
  if (receiverInfo.middleName) flat["receiver.name.middleName"] = receiverInfo.middleName;
  if (receiverInfo.lastName) flat["receiver.name.lastName"] = receiverInfo.lastName;
  if (receiverInfo.secondLastName) flat["receiver.name.secondLastName"] = receiverInfo.secondLastName;

  const addr = senderAddress as unknown as Record<string, string>;
  for (const tag of mapiSenderAddressFieldDisplayOrder) {
    const v = (addr[tag] ?? "").trim();
    if (!v) continue;
    if (tag === "sender.address.countrySubdivisionCode") {
      flat[tag] = normalizeUsCountrySubdivisionCode(v);
    } else if (tag === "sender.address.countryCode") {
      flat[tag] = v.toUpperCase();
    } else {
      flat[tag] = v;
    }
  }

  for (const [key, val] of Object.entries(senderPersonalDetails)) {
    if (!val?.trim()) continue;
    flat[key] = key.includes("dateOfBirth") ? toIsoDateOfBirth(val.trim()) : val.trim();
  }

  if (journey.senderDob && !flat["sender.personalDetails.dateOfBirth"]) {
    flat["sender.personalDetails.dateOfBirth"] = toIsoDateOfBirth(journey.senderDob);
  }

  const phone = phoneNumber.replace(/\D/g, "");
  if (phone) {
    flat["sender.mobilePhone.number"] = phone;
    flat["sender.mobilePhone.countryDialCode"] = "1";
  }

  for (const [key, val] of Object.entries(senderPrimaryIdentification)) {
    if (!val?.trim()) continue;
    if (key === "sender.primaryIdentification.issueCountrySubdivisionCode") {
      flat[key] = normalizeUsCountrySubdivisionCode(val.trim());
    } else if (key === "sender.primaryIdentification.issueCountryCode") {
      flat[key] = val.trim().toUpperCase();
    } else {
      flat[key] = val.trim();
    }
  }

  for (const [key, val] of Object.entries(senderSecondaryIdentification)) {
    if (!val?.trim()) continue;
    if (key === "sender.secondaryIdentification.issueCountrySubdivisionCode") {
      flat[key] = normalizeUsCountrySubdivisionCode(val.trim());
    } else if (key === "sender.secondaryIdentification.issueCountryCode") {
      flat[key] = val.trim().toUpperCase();
    } else {
      flat[key] = val.trim();
    }
  }

  if (!flat["sender.primaryIdentification.id"] && additionalInfo.identityNumber) {
    flat["sender.primaryIdentification.id"] = additionalInfo.identityNumber;
  }
  if (
    !flat["sender.primaryIdentification.issueCountryCode"] &&
    additionalInfo.issueCountry
  ) {
    flat["sender.primaryIdentification.issueCountryCode"] =
      additionalInfo.issueCountry.toUpperCase();
  }
  if (
    !flat["sender.primaryIdentification.issueCountrySubdivisionCode"] &&
    additionalInfo.issueState
  ) {
    flat["sender.primaryIdentification.issueCountrySubdivisionCode"] =
      normalizeUsCountrySubdivisionCode(additionalInfo.issueState);
  }

  // Additional required fields collected on the Additional Info screen.
  for (const [key, val] of Object.entries(additionalFields)) {
    if (!val?.trim()) continue;
    flat[key] = val.trim();
  }

  const nested = dotKeysToNested(flat);

  const updateData: UpdateTransactionRequest["updateData"] = {
    targetAudience: "CONSUMER_FACING",
    agentPartnerId,
    destinationCountryCode: journey.destinationCountry.iso3Code,
    serviceOptionCode: option.serviceOptionCode || quoteFeeRequestDefaults.serviceOptionCode,
    sendAmount: {
      value: journey.sendAmount,
      currencyCode: journey.sendCurrency || "USD",
    },
    receiveCurrencyCode: journey.receiveCurrency,
    fundInStore: {
      fundInStore: quoteFeeRequestDefaults.fundInStore,
      fundInStoreAgentPartnerId: agentPartnerId,
    },
  };

  if (nested.receiver) {
    updateData.receiver = nested.receiver as UpdateTransactionRequest["updateData"]["receiver"];
  }
  if (nested.sender) {
    updateData.sender = nested.sender as UpdateTransactionRequest["updateData"]["sender"];
  }

  // Include any additional groups (e.g. biographical) collected outside sender/receiver.
  for (const [group, value] of Object.entries(nested)) {
    if (group === "sender" || group === "receiver") continue;
    (updateData as unknown as Record<string, unknown>)[group] = value;
  }

  if (option.serviceOptionRoutingCode) {
    updateData.serviceOptionRoutingCode = option.serviceOptionRoutingCode;
  }

  const destState = journey.destinationState?.code;
  if (destState) {
    updateData.destinationCountrySubdivisionCode =
      normalizeUsCountrySubdivisionCode(destState);
  }

 

  return { transactionId, updateData };
}
